import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  TechnicalServiceCategoryKey,
  TechnicalServiceCategoryOrder,
  TechnicalServiceHeaderOrder,
  TECHNICAL_SERVICE_CATEGORY_ORDER,
  createDefaultTechnicalServiceCategoryOrder
} from '../../utils/technical-service-categories';
import { TechnicalServiceSelectionChange } from './technical-services-ordering.types';
import { TechnicalServiceSelectOptionGroup } from './technical-services-ordering.types';
import { TechnicalServicesOrderingService } from './technical-services-ordering.service';

@Component({
  selector: 'app-technical-services-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzSelectModule,
    NzTagModule,
    NzCollapseModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    DragDropModule
  ],
  templateUrl: './technical-services-selector.component.html',
  styleUrls: ['./technical-services-selector.component.less']
})
export class TechnicalServicesSelectorComponent implements OnChanges {
  @Input() selectedHeaderKeys: string[] = [];
  @Input() headerOrder: TechnicalServiceHeaderOrder | null = null;
  @Input() categoryOrder: TechnicalServiceCategoryOrder | null = null;
  @Input() landingPageEmergencyServicePlacementEnabled = false;
  @Input() landingPageEmergencyServiceTypes: string[] = [];
  @Input() showSelectionRequiredError = false;

  @Output() selectionChange = new EventEmitter<TechnicalServiceSelectionChange>();
  @Output() orderChange = new EventEmitter<TechnicalServiceSelectionChange>();
  @Output() emergencyPlacementChange = new EventEmitter<string[]>();

  readonly defaultCategoryOrder = TECHNICAL_SERVICE_CATEGORY_ORDER;
  readonly optionGroups: TechnicalServiceSelectOptionGroup[] = [];
  connectedDropListIds: string[] = [];

  technicalServicesSelectOpen = false;

  categoryOrderInternal: TechnicalServiceCategoryOrder = createDefaultTechnicalServiceCategoryOrder();
  selectedHeaderKeysInternal: string[] = [];
  headerOrderInternal: TechnicalServiceHeaderOrder;
  selectedHeaderKeysInOrder: string[] = [];
  emergencyServiceTypesInternal: string[] = [];

  constructor(private orderingService: TechnicalServicesOrderingService) {
    this.optionGroups = this.orderingService.selectGroups;
    this.connectedDropListIds = this.defaultCategoryOrder.map((categoryKey) =>
      this.orderingService.getCategoryDropListId(categoryKey)
    );
    this.headerOrderInternal = this.orderingService.normalizeHeaderOrder({}, []);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedHeaderKeys']
      || changes['headerOrder']
      || changes['categoryOrder']
      || changes['landingPageEmergencyServicePlacementEnabled']
      || changes['landingPageEmergencyServiceTypes']
    ) {
      this.syncStateFromInputs();
    }
  }

  onSelectedHeaderKeysChange(headerKeys: string[]): void {
    this.selectedHeaderKeysInternal = this.orderingService.normalizeSelectedHeaderKeys(headerKeys);
    this.headerOrderInternal = this.orderingService.applySelectionToOrder(
      this.selectedHeaderKeysInternal,
      this.headerOrderInternal
    );
    this.refreshOrderedPreview();
    this.selectionChange.emit(this.buildPayload());
  }

  onCategoryDrop(event: CdkDragDrop<TechnicalServiceCategoryOrder>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.categoryOrderInternal, event.previousIndex, event.currentIndex);
    this.refreshOrderedPreview();
    this.orderChange.emit(this.buildPayload());
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.refreshOrderedPreview();
    this.orderChange.emit(this.buildPayload());
  }

  getCategoryDropListId(categoryKey: TechnicalServiceCategoryKey): string {
    return this.orderingService.getCategoryDropListId(categoryKey);
  }

  getServiceTranslationKey(headerKey: string): string {
    const service = this.orderingService.getServiceByHeaderKey(headerKey);
    return `TECHNICAL_SERVICES.${service?.translationKey || headerKey}`;
  }

  onTechnicalServicesSelectOpenChange(open: boolean): void {
    this.technicalServicesSelectOpen = open;
  }

  stopTechnicalServicesCtaEvent(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  }

  toggleTechnicalServicesSelect(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    this.technicalServicesSelectOpen = !this.technicalServicesSelectOpen;
  }

  isEmergencyPlacementActive(headerKey: string): boolean {
    const serviceType = this.orderingService.getServiceByHeaderKey(headerKey)?.serviceType;
    return !!serviceType && this.emergencyServiceTypesInternal.includes(serviceType);
  }

  toggleEmergencyPlacement(headerKey: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const serviceType = this.orderingService.getServiceByHeaderKey(headerKey)?.serviceType;
    if (!serviceType) {
      return;
    }

    this.emergencyServiceTypesInternal = this.isEmergencyPlacementActive(headerKey)
      ? this.emergencyServiceTypesInternal.filter((type) => type !== serviceType)
      : [...this.emergencyServiceTypesInternal, serviceType];
    this.emergencyPlacementChange.emit([...this.emergencyServiceTypesInternal]);
  }

  private syncStateFromInputs(): void {
    this.categoryOrderInternal = this.orderingService.normalizeCategoryOrder(this.categoryOrder);
    this.selectedHeaderKeysInternal = this.orderingService.normalizeSelectedHeaderKeys(this.selectedHeaderKeys);
    this.headerOrderInternal = this.orderingService.normalizeHeaderOrder(
      this.headerOrder || {},
      this.selectedHeaderKeysInternal
    );
    this.emergencyServiceTypesInternal = Array.from(new Set(
      (Array.isArray(this.landingPageEmergencyServiceTypes) ? this.landingPageEmergencyServiceTypes : [])
        .filter((type) => typeof type === 'string' && type.trim())
        .map((type) => type.trim())
    ));
    this.refreshOrderedPreview();
  }

  private refreshOrderedPreview(): void {
    this.selectedHeaderKeysInOrder = this.orderingService.getOrderedSelectedHeaderKeysByCategoryOrder(
      this.headerOrderInternal,
      this.categoryOrderInternal
    );
  }

  private buildPayload(): TechnicalServiceSelectionChange {
    return {
      categoryOrder: [...this.categoryOrderInternal],
      selectedHeaderKeys: [...this.selectedHeaderKeysInternal],
      headerOrder: this.orderingService.normalizeHeaderOrder(
        this.headerOrderInternal,
        this.selectedHeaderKeysInternal
      )
    };
  }
}
